import React, { useState, useRef } from "react";
import { Outlet } from "react-router-dom";
import { Grid3X3, PenSquare, Sun, Moon } from "lucide-react";
import { useAuthMe } from "../hooks/useAuthMe";
import { BizeyeSidebar } from "./sidebar/BizeyeSidebar";
import { useTheme } from "../contexts/ThemeContext";
import { SenrysaLogo } from "../Components/SenrysaLogo";
import { parseAssignedApplications } from "../utils/matchesAssignedApplicationOrigin";

function useIsEmbeddedFrame(): boolean {
  return typeof window !== "undefined" && window.self !== window.top;
}

export const MainLayout: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { data: authUser } = useAuthMe();
  const inIframe = useIsEmbeddedFrame();
  const [bentoMenuOpen, setBentoMenuOpen] = useState(false);
  const bentoMenuRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (bentoMenuRef.current && !bentoMenuRef.current.contains(e.target as Node)) {
        setBentoMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const assignedApplications = React.useMemo(
    () => parseAssignedApplications(authUser?.applications),
    [authUser?.applications],
  );

  if (inIframe) {
    return (
      <div className="flex h-screen min-h-0 flex-col overflow-hidden bg-slate-100 dark:bg-[#0B0F19]">
        <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-100 text-slate-900 dark:bg-[#0B0F19] dark:text-slate-100">
      <header className="relative z-[200] flex h-16 flex-shrink-0 items-center justify-between border-b border-slate-200 bg-white px-6 dark:border-slate-800/70 dark:bg-[#0B0F19]">
        <div className="flex items-center gap-3">
          <SenrysaLogo className="p-1" />
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-lg p-2.5 text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#1C202F]/55 dark:hover:text-slate-200"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Light mode" : "Dark mode"}
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          {assignedApplications.length > 0 && (
            <div className="relative" ref={bentoMenuRef}>
              <button
                type="button"
                onClick={() => setBentoMenuOpen((b) => !b)}
                className="rounded-lg p-2.5 text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#1C202F]/55 dark:hover:text-slate-200"
                aria-label="Apps menu"
              >
                <Grid3X3 className="h-5 w-5" />
              </button>
              {bentoMenuOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700/80 dark:bg-[#1C202F]/95">
                  <div className="p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-900 dark:text-white">Your favourites</span>
                      <button
                        type="button"
                        className="rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#1C202F]/60 dark:hover:text-slate-200"
                        aria-label="Edit favourites"
                      >
                        <PenSquare className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="border-t border-slate-200 pt-3 dark:border-slate-700/80">
                      <div className="grid grid-cols-2 gap-3">
                        {assignedApplications.map((application) => (
                          <a
                            key={application.id}
                            href={application.url}
                            onClick={() => setBentoMenuOpen(false)}
                            className="flex flex-col items-center gap-2 rounded-lg p-3 transition-colors hover:bg-slate-100 dark:hover:bg-[#1C202F]/55"
                          >
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100 p-2 dark:bg-[#2A3248]">
                              <img
                                src={`${application.url.replace(/\/$/, "")}/favicon.ico`}
                                alt={`${application.name} favicon`}
                                className="h-7 w-7 rounded object-contain"
                                loading="lazy"
                                onError={(event) => {
                                  event.currentTarget.src = "/favicon.ico";
                                }}
                              />
                            </div>
                            <span className="text-center text-xs text-slate-600 dark:text-slate-400">{application.name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <BizeyeSidebar authUser={authUser} />
        <main className="flex h-full flex-1 flex-col overflow-hidden bg-slate-100 dark:bg-[#0B0F19]">
          <div className="flex h-full flex-1 flex-col overflow-y-auto p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
